import { Modal, Select } from '@/components';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import '@/assets/styles/components/new-ticket-modal.styles.scss';
import { useMemo, useState } from 'react';
import { SelectOption } from '@/components/select/select.types.ts';
import clsx from 'clsx';
import { SupportTicketCreateRequest } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { useSizes } from '@/utility/responsive.ts';
import { Choose, Otherwise, When } from "vite-plugin-react-control-statements";

interface Props {
	isOpen: boolean;
	onClose: () => void;
	studentId?: number;
}

const deviceTypeOptions: SelectOption[] = [
    { label: 'Web', value: 'WEB' },
    { label: 'Mobil', value: 'MOBILE' },
    { label: 'Tablet', value: 'TABLET' }
];

export const supportAppList = [
    {
        label: 'Doping Hafıza Hakkında',
        value: 'ONLINE_EDUCATION',
        serviceGroup: 'ONLINE_EDUCATION',
        liveServiceType: ''
    },
    {
        label: 'Koçum Yanımda Anlık',
        value: 'GUIDANCE',
        serviceGroup: 'COACHING',
        liveServiceType: 'GUIDANCE'
    },
    {
        label: 'Koçum Yanımda Randevulu',
        value: 'ONE_ON_ONE_COACHING',
        serviceGroup: 'COACHING',
        liveServiceType: 'ONE_ON_ONE_COACHING'
    },
    {
        label: 'Şimdi Anladım Anlık',
        value: 'LECTURE',
        serviceGroup: 'GOT_IT',
        liveServiceType: 'LECTURE'
    },
    {
        label: 'Şimdi Anladım Randevulu',
        value: 'ONE_ON_ONE_LECTURE',
        serviceGroup: 'GOT_IT',
        liveServiceType: 'ONE_ON_ONE_LECTURE'
    },
    {
        label: 'Çözücü',
        value: 'QUESTION_SOLUTION',
        serviceGroup: 'SOLVER',
        liveServiceType: 'QUESTION_SOLUTION'
    }
];

const NewSupportTicketModal = ({ isOpen, onClose, studentId }: Props) => {
	const isExpertTicket = studentId === undefined;
	const { isMobile } = useSizes();
	const { SupportTicketControllerApi } = useApi();
	const [selectedDeviceType, setSelectedDeviceType] = useState<string | undefined>(undefined);
	const [selectedAppValue, setSelectedAppValue] = useState<string | undefined>(undefined);
	const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
	const [message, setMessage] = useState<string>('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const queryClient = useQueryClient();

	const selectedApp = useMemo(() => supportAppList.find(a => a.value === selectedAppValue), [selectedAppValue]);

	const appOptions: SelectOption[] = useMemo(
		() => supportAppList.map(item => ({ label: item.label, value: item.value })),
		[]
	);

        const { data: categories } = useQueryProvider(
		[QUERY_KEYS.getSupportTicketCategories, selectedApp?.serviceGroup ?? '', selectedApp?.liveServiceType ?? ''],
		() =>
			SupportTicketControllerApi.getSupportTicketCategories(
				selectedApp!.serviceGroup as Parameters<typeof SupportTicketControllerApi.getSupportTicketCategories>[0],
				(selectedApp!.liveServiceType as Parameters<typeof SupportTicketControllerApi.getSupportTicketCategories>[1]) || undefined
			),
		{
			select: ({ data }) => data?.data,
			enabled: !!selectedApp?.serviceGroup,
			refetchOnWindowFocus: false
		}
	);

	const categoryOptions: SelectOption[] = useMemo(
		() =>
			categories?.map(cat => ({
				label: cat.name as string,
				value: cat.id as unknown as string
			})) || [],
		[categories]
	);


	const createTicket = useMutationProvider((body: SupportTicketCreateRequest) => SupportTicketControllerApi.createSupportTicket(body), {
		onSuccess: () => {
			toast.success('Destek talebi başarıyla oluşturuldu');
		},
		onError: e => {
			throw e;
		}
	});

	const selectedCategoryLabel = useMemo(
		() => categoryOptions.find(o => String(o.value) === String(selectedCategoryId))?.label ?? '',
		[categoryOptions, selectedCategoryId]
	);

	const onSubmit = async () => {
		setIsSubmitting(true);
		try {
			const body = {
				...(!isExpertTicket && { relatedStudentId: studentId }),
				categoryId: selectedCategoryId,
				priority: null,
				subject: selectedCategoryLabel,
				content: message,
				deviceType: selectedDeviceType
			};

			await createTicket.mutateAsync(body);
			await queryClient.invalidateQueries(QUERY_KEYS.getSupportTickets);

			handleClose();
		} catch {
			toast.error('Destek talebi oluşturulurken bir hata oluştu');
		} finally {
			setIsSubmitting(false);
		}
	};

	const isFormValid =
		!!selectedDeviceType &&
		!!selectedAppValue &&
		(!selectedApp?.serviceGroup || !!selectedCategoryId) &&
		message.trim().length >= 25;

	const handleClose = () => {
		setSelectedDeviceType(undefined);
		setSelectedAppValue(undefined);
		setSelectedCategoryId(undefined);
		setMessage('');
		onClose();
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			closeOnEsc={false}
			closeOnClickOutside={false}
			className="new-ticket-modal">
			<div
				className="close-icon"
				onClick={handleClose}>
				<i className="icons-close" />
			</div>
			<div className="modal-title">
                <Choose>
                    <When condition={isExpertTicket}>
                        Yeni Uzman Destek Talebi
                    </When>
                    <Otherwise>
                        Yeni Öğrenci Destek Talebi
                    </Otherwise>
                </Choose>
            </div>
			<form>
				<Select
					options={deviceTypeOptions}
					label="Sorun Yaşanan Cihaz Tipi"
					value={selectedDeviceType}
					placeholder="Cihaz tipi seçin"
					onChange={e => setSelectedDeviceType(e.target.value)}
				/>
				<Select
					options={appOptions}
					label="Kategori"
					value={selectedAppValue}
					placeholder="Kategori seçin"
					onChange={e => {
						setSelectedAppValue(e.target.value);
						setSelectedCategoryId(undefined);
					}}
				/>
				<Select
					options={categoryOptions}
					label="Alt Kategori"
					value={selectedCategoryId}
					placeholder="Alt kategori seçin"
					disabled={!selectedAppValue || !selectedApp?.serviceGroup}
					onChange={e => setSelectedCategoryId(Number(e.target.value))}
				/>
				<div className="input-wrapper">
					<label
						htmlFor="message"
						className="message-title">
						Mesaj
					</label>
					<textarea
						className="message-input"
						placeholder="Mesajınızı buraya yazabilirsiniz..."
						value={message}
						maxLength={500}
						onChange={e => setMessage(e.target.value)}
					/>
					{isMobile && (
						<div className="chat-input-footer">
							<button
								type="button"
								onClick={onSubmit}
								className={clsx('submit-button', { disabled: !isFormValid })}
								disabled={!isFormValid || isSubmitting}>
								<i className="icons-plain" />
							</button>
						</div>
					)}
				</div>
				{!isMobile && (
					<button
						type="button"
						onClick={onSubmit}
						className={clsx('submit-button', { disabled: !isFormValid })}
						disabled={!isFormValid || isSubmitting}>
						{isSubmitting ? 'Oluşturuluyor...' : 'Oluştur'}
					</button>
				)}
			</form>
		</Modal>
	);
};

export default NewSupportTicketModal;
